import { useEffect } from 'react';
import Head from 'next/head';

/**
 * Custom hook to set page titles dynamically
 * Uses Next.js Head component for proper SSR support
 * @param {string} title - The page title (will be appended with " | PLAVED")
 * @param {Object} dependencies - Optional dependencies object for dynamic titles
 */
export const usePageTitle = (title, dependencies = {}) => {
  // Build the full title
  let processedTitle = title;
  
  // Replace placeholders in title with actual values
  Object.keys(dependencies).forEach(key => {
    const placeholder = `{${key}}`;
    if (processedTitle && processedTitle.includes(placeholder)) {
      processedTitle = processedTitle.replace(placeholder, dependencies[key] || '');
    }
  });
  
  // Always append PLAVED to maintain brand consistency
  const fullTitle = processedTitle ? `${processedTitle} | PLAVED` : 'PLAVED';
  
  // Set document.title immediately for client-side navigation
  useEffect(() => {
    // Use requestAnimationFrame to ensure title persists
    const frame = requestAnimationFrame(() => {
      document.title = fullTitle;
    });
    
    // Keep setting the title on a short interval to combat any overwrites
    const interval = setInterval(() => {
      if (document.title !== fullTitle) {
        document.title = fullTitle;
      }
    }, 100);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(frame);
      clearInterval(interval);
    };
  }, [fullTitle]);
  
  // Return the Head component for SSR
  return (
    <Head>
      <title key="title">{fullTitle}</title>
      <meta property="og:title" content={fullTitle} key="og:title" />
    </Head>
  );
};